import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/production/outsource`;

const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => toDateStr(new Date());
const addDays = (s, n) => {
  const [y, m, d] = s.split("-").map(Number);
  return toDateStr(new Date(y, m - 1, d + n));
};
const fmtDay = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtStamp = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "";
const validDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "");

const titles = {
  first: "Production Date",
  update: "Update Current Batch",
  new_batch: "New Batch",
  new_date: "New Date",
};

function Card({ label, value, tone }) {
  const tones = { blue: "bg-blue-50 border-blue-200", green: "bg-green-50 border-green-200", grey: "bg-gray-50 border-gray-200" };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone || "grey"]}`}>
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

export default function OutsourceSyncButton({ refreshKey }) {
  const [status, setStatus] = useState(null);
  const [stage, setStage] = useState(null); // null | confirm | choose | plan
  const [mode, setMode] = useState("first");
  const [date, setDate] = useState(today());
  const [plan, setPlan] = useState(null);
  const [customBatch, setCustomBatch] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [planError, setPlanError] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`);
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus, refreshKey]);

  const loadPlan = useCallback(async (m, d) => {
    setLoadingPlan(true);
    setPlanError("");
    try {
      const r = await axios.get(`${API}/preview`, { params: { mode: m, date: d } });
      setPlan(r.data);
      if (r.data && r.data.batch_number) setCustomBatch(r.data.batch_number);
      if (r.data && r.data.new_files) setSelectedFiles(r.data.new_files.map((f) => f.file_uid));
    } catch (e) {
      setPlan(null);
      setPlanError((e.response && e.response.data && e.response.data.detail) || "Could not load the preview.");
    }
    setLoadingPlan(false);
  }, []);

  useEffect(() => {
    if (stage === "plan" && (mode === "update" || validDate(date))) loadPlan(mode, date);
  }, [stage, mode, date, loadPlan]);

  const synced = status && status.state === "synced";
  const disabled = !status || status.state === "empty";

  const open = () => {
    if (disabled) return;
    setPlan(null);
    setPlanError("");
    setStage(status.state === "unsynced" ? "confirm" : "choose");
  };

  const startPlan = async (m) => {
    let d = today();
    if (m === "new_batch" || m === "new_date") {
      try {
        const r = await axios.get(`${API}/preview`, { params: { mode: "update" } });
        if (r.data && r.data.date) d = m === "new_date" ? addDays(r.data.date, 1) : r.data.date;
      } catch (e) { /* keep today */ }
    }
    setPlan(null);
    setCustomBatch(null);
    setSelectedFiles([]);
    setMode(m);
    setDate(d);
    setStage("plan");
  };

  const commit = async () => {
    setSaving(true);
    try {
      let user = "";
      try { user = JSON.parse(localStorage.getItem("user") || "{}").name || ""; } catch (e) { user = ""; }
      const r = await axios.post(`${API}/commit`, { mode, date, user, batch_number: customBatch, selected_file_uids: selectedFiles.length > 0 ? selectedFiles : null });
      if (r.data.created) {
        toast.success(`Saved to Outsourced Work: ${fmtDay(r.data.batch.production_date)} B${r.data.batch.batch_number} - ${r.data.new_sheets} new sheets`);
      } else {
        toast.info(r.data.message || "Nothing new to sync.");
      }
      setStage(null);
      await loadStatus();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || "Sync failed. Nothing was saved.");
    }
    setSaving(false);
  };

  const back = () => setStage(mode === "first" ? "confirm" : "choose");

  const buttonClass = disabled
    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
    : synced
    ? "bg-green-600 text-white hover:bg-green-700"
    : "bg-blue-600 text-white hover:bg-blue-700";

  const confirmLabel =
    mode === "update" ? `UPDATE ${plan ? plan.batch_label : "BATCH"}` : mode === "new_batch" ? `CREATE ${plan ? plan.batch_label : "BATCH"}` : "CREATE & SYNC";
  const canCommit = !saving && !loadingPlan && plan && plan.has_new && selectedFiles.length > 0 && (mode === "update" || validDate(date));
  const warnCount = plan ? plan.new_files.filter((f) => f.same_name_warning).length : 0;

  return (
    <>
      <button
        onClick={open}
        disabled={disabled}
        className={`rounded-md px-3 py-1 text-xs font-semibold ${buttonClass}`}
        title={synced ? "Already synced - click for options" : "Sync this batch to Outsourced Work"}
      >
        {synced ? `SYNCED • ${fmtStamp(status.last_synced_at)}` : "SYNC"}
        {synced && status.new_count > 0 && (
          <span className="ml-1.5 rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] text-white">+{status.new_count} new</span>
        )}
      </button>

      <Dialog open={stage !== null} onOpenChange={(o) => { if (!o && !saving) setStage(null); }}>
        <DialogContent className="max-w-md">
          {stage === "confirm" && (
            <>
              <DialogHeader>
                <DialogTitle>Please confirm if today's print batch is ready?</DialogTitle>
                <DialogDescription>
                  Make sure all production files for this print batch are ready before syncing. You can add more files later using Update Current Batch.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setStage(null)} className="rounded-md border border-orange-400 px-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50">
                  NOT READY
                </button>
                <button onClick={() => startPlan("first")} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                  CONFIRM
                </button>
              </div>
            </>
          )}

          {stage === "choose" && (
            <>
              <DialogHeader>
                <DialogTitle>Add to Outsourced Work</DialogTitle>
                <DialogDescription>These files are already synced. Choose what to do with any new files.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <button onClick={() => startPlan("update")} className="w-full rounded-lg border border-blue-300 p-3 text-left hover:bg-blue-50">
                  <p className="text-sm font-semibold text-blue-700">UPDATE CURRENT BATCH</p>
                  <p className="text-xs text-gray-500">Add only the new files to the batch you already synced. No new batch.</p>
                </button>
                <button onClick={() => startPlan("new_batch")} className="w-full rounded-lg border border-blue-300 p-3 text-left hover:bg-blue-50">
                  <p className="text-sm font-semibold text-blue-700">NEW BATCH</p>
                  <p className="text-xs text-gray-500">Create the next batch number (B2, B3...) for a date.</p>
                </button>
                <button onClick={() => startPlan("new_date")} className="w-full rounded-lg border border-blue-300 p-3 text-left hover:bg-blue-50">
                  <p className="text-sm font-semibold text-blue-700">NEW DATE</p>
                  <p className="text-xs text-gray-500">Sync the new files under a different production date.</p>
                </button>
              </div>
              <div className="flex justify-end pt-1">
                <button onClick={() => setStage(null)} className="rounded-md border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
              </div>
            </>
          )}

          {stage === "plan" && (
            <>
              <DialogHeader>
                <DialogTitle>{titles[mode]}</DialogTitle>
                <DialogDescription>
                  {mode === "update"
                    ? "Only files that are not already synced are added. No new batch is created."
                    : "Choose the production date this batch belongs to. It does not have to be today."}
                </DialogDescription>
              </DialogHeader>

              {mode !== "update" && (
                <div>
                  <label className="mb-1 block text-xs font-bold text-gray-700">Production Date</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" />
                </div>
              )}

              {loadingPlan && <p className="py-3 text-center text-sm text-gray-400">Loading preview...</p>}
              {planError && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">{planError}</p>}

              {plan && !loadingPlan && (
                <div className="space-y-3">
                  {mode === "update" ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Card label={`Existing Sheets (${plan.batch_label}, ${fmtDay(plan.date)})`} value={plan.existing_sheets} />
                      <Card label="Selected Files" value={selectedFiles.length} tone="blue" />
                      <Card label="Selected Sheets" value={plan.new_files.filter(f => selectedFiles.includes(f.file_uid)).reduce((s,f) => s+f.sheets, 0)} tone="green" />
                      <Card label="Updated Total" value={plan.updated_total} tone="green" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Card label="Production Date" value={fmtDay(plan.date)} />
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-2">
                        <p className="text-xs text-blue-600 font-medium mb-1">Batch Number</p>
                        <input
                          type="number"
                          min="1"
                          value={customBatch ?? plan.batch_number}
                          onChange={e => setCustomBatch(Number(e.target.value))}
                          className="w-full text-sm font-bold text-blue-700 bg-transparent border-b border-blue-300 focus:outline-none text-center"
                        />
                        {customBatch !== plan.batch_number && (
                          <p className="text-xs text-orange-600 mt-1">⚠️ Changed from suggested B{plan.batch_number}</p>
                        )}
                      </div>
                      <Card label="Selected Files" value={selectedFiles.length} tone="blue" />
                      <Card label="Selected Sheets" value={plan.new_files.filter(f => selectedFiles.includes(f.file_uid)).reduce((s,f) => s+f.sheets, 0)} tone="green" />
                    </div>
                  )}

                  {warnCount > 0 && (
                    <p className="rounded-md border border-orange-300 bg-orange-50 p-2 text-xs text-orange-700">
                      {warnCount} file(s) have the same name as files already synced. If this is the same work made again, its sheets will be counted again.
                    </p>
                  )}

                  {plan.new_files.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-gray-600">Select PDFs to include in this batch:</p>
                        <button className="text-xs text-blue-600" onClick={() =>
                          selectedFiles.length === plan.new_files.length
                            ? setSelectedFiles([])
                            : setSelectedFiles(plan.new_files.map((f) => f.file_uid))
                        }>
                          {selectedFiles.length === plan.new_files.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="max-h-40 overflow-y-auto rounded-md border">
                        {plan.new_files.map((f) => (
                          <div key={f.file_uid} className="flex items-center gap-2 border-b px-3 py-1.5 text-xs last:border-b-0 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setSelectedFiles((prev) =>
                              prev.includes(f.file_uid) ? prev.filter((id) => id !== f.file_uid) : [...prev, f.file_uid]
                            )}>
                            <input type="checkbox" readOnly checked={selectedFiles.includes(f.file_uid)} className="cursor-pointer" />
                            <span className="flex-1">{f.name}{f.same_name_warning && <span className="ml-1 rounded bg-orange-100 px-1 text-orange-700">same name</span>}</span>
                            <span className="font-semibold">{f.sheets} sheets</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedFiles.length} of {plan.new_files.length} PDFs selected
                        ({plan.new_files.filter((f) => selectedFiles.includes(f.file_uid)).reduce((s, f) => s + f.sheets, 0)} sheets)
                      </p>
                    </div>
                  )}

                  {!plan.has_new && (
                    <p className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-700">{plan.message}</p>
                  )}
                  {plan.skipped && plan.skipped.length > 0 && (
                    <p className="text-xs text-gray-500">Skipped (sheet count unavailable): {plan.skipped.join(", ")}</p>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button onClick={back} disabled={saving} className="rounded-md border px-4 py-2 text-sm hover:bg-gray-50">Back</button>
                <button
                  onClick={commit}
                  disabled={!canCommit}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-600"
                >
                  {saving ? "Saving..." : confirmLabel}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
